import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

export interface BookSummaryDto {
  id: string;
  label: string;
  versionNumber: number;
  isPrimary: boolean;
  stage: string | null;
  sourceOutlineId: string | null;
  sourceOutlineLabel: string | null;
  chapterCount: number;
  updatedAt: string;
}

export interface OutlineSummaryDto {
  id: string;
  label: string;
  versionNumber: number;
  isPrimary: boolean;
  stage: string | null;
  sourceModel: string | null;
  hierarchyPreset: string | null;
  customInstructions: string | null;
  temperature: number | null;
  nodeCount: number;
  updatedAt: string;
}

export interface ProjectDto {
  id: string;
  name: string;
  description: string | null;
  status: string | null;
  templateId: string | null;
  books: BookSummaryDto[];
  outlines: OutlineSummaryDto[];
  sessionId: string | null;
  architectId: string | null;
  boardId: string | null;
  worldCount: number;
  entityCount: number;
  updatedAt: string;
}

export interface ProjectSummaryDto {
  id: string;
  name: string;
  description: string | null;
  status: string | null;
  bookCount: number;
  outlineCount: number;
  updatedAt: string;
}

export interface ProjectTemplateDto {
  id: string;
  name: string;
  description?: string | null;
  source: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  traceId?: string;
}

export function useProjects() {
  const { getToken } = useAuth();
  return useQuery<ProjectSummaryDto[]>({
    queryKey: ['projects'],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const response = await client
        .get('api/v2/projects?page_size=100')
        .json<ApiResponse<ProjectSummaryDto[]>>();
      return response.data ?? [];
    },
  });
}

export function useProject(projectId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<ProjectDto>({
    enabled: !!projectId,
    queryKey: ['project', projectId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const response = await client
        .get(`api/v2/projects/${projectId}`)
        .json<ApiResponse<ProjectDto>>();
      return response.data;
    },
  });
}

export interface CreateProjectInput {
  name: string;
  description?: string;
  templateId?: string;
}

export function useCreateProject() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateProjectInput) => {
      const client = createApiClient(getToken);
      const response = await client
        .post('api/v2/projects', { json: input })
        .json<ApiResponse<ProjectDto>>();
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export interface UpdateProjectInput {
  id: string;
  name: string;
  description?: string;
}

export function useUpdateProject() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateProjectInput) => {
      const client = createApiClient(getToken);
      const response = await client
        .put(`api/v2/projects/${input.id}`, {
          json: { name: input.name, description: input.description },
        })
        .json<ApiResponse<ProjectDto>>();
      return response.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['project', data.id] });
    },
  });
}

export function useDeleteProject() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/projects/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function useProjectTemplates() {
  const { getToken } = useAuth();
  return useQuery<ProjectTemplateDto[]>({
    queryKey: ['project-templates'],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const response = await client
        .get('api/v2/project-templates')
        .json<ApiResponse<ProjectTemplateDto[]>>();
      return response.data ?? [];
    },
  });
}
